import { ParsedData, ExampleRatings, ModelSummary, Example } from '../types';
import { getModelInferenceStats, getModelConfidenceStats } from './inferenceUtils';
import { ratingToCategory } from './scoreUtils';
import { HUMAN_MODEL } from './entityUtils';
import { computeModelMetrics } from './evaluation';

/**
 * BQ-19: `examplesOverride` lets callers export a filtered subset (the
 * filter-aware export scope chooser passes the already-filtered list). When
 * provided, both the JSON `examples` array and the computed `modelSummaries`
 * are restricted to that subset; `metadata.totalQuestions` reflects override
 * length so consumers downstream can tell scope at a glance.
 */
export function exportToJSON(
  data: ParsedData,
  scores: ExampleRatings,
  examplesOverride?: Example[]
): string {
  const exportExamples = examplesOverride ?? data.examples;
  const enhancedData: any = {
    metadata: {
      exportDate: new Date().toISOString(),
      totalQuestions: exportExamples.length,
      models: data.modelNames
    },
    // BQ-18: round-trip per-label annotation guidelines if present. Placed
    // at the top level (mirrors `customLabelColors` / `savedThemes`) so the
    // schema stays additive — old loaders ignore the unknown key.
    ...(data.labelDefinitions && Object.keys(data.labelDefinitions).length > 0
      ? { labelDefinitions: data.labelDefinitions }
      : {}),
    examples: exportExamples.map(example => {
      const exampleData = {
        id: example.id,
        text: example.text,
        humanAnnotations: example.humanAnnotations?.map(e => ({
          text: e.text,
          label: e.label,
          start: e.start,
          end: e.end
        })),
        // BQ-15: round-trip rejected predictions so the export reflects the
        // user's triage state (and re-importing restores it).
        rejectedPredictions: example.rejectedPredictions?.map(r => ({
          modelName: r.modelName,
          entity: {
            text: r.entity.text,
            label: r.entity.label,
            start: r.entity.start,
            end: r.entity.end,
            confidence: r.entity.confidence
          }
        })),
        models: example.modelResponses.map(response => {
          const entities = response.entities;
          const confidenceScores = entities
            .filter(e => e.confidence !== undefined)
            .map(e => e.confidence!);

          const avgConfidence = confidenceScores.length > 0
            ? confidenceScores.reduce((sum, s) => sum + s, 0) / confidenceScores.length
            : null;

          const score = scores[example.id]?.[response.modelName];

          return {
            modelName: response.modelName,
            inferenceTime: response.inferenceTime,
            entityCount: entities.length,
            avgConfidence,
            entities: entities.map(e => ({
              text: e.text,
              label: e.label,
              start: e.start,
              end: e.end,
              confidence: e.confidence
            })),
            rating: score?.rating,
            notes: score?.notes
          };
        })
      };
      return exampleData;
    }),
    modelSummaries: calculateModelSummaries(data, scores, exportExamples)
  };

  return JSON.stringify(enhancedData, null, 2);
}

export function exportToCSV(data: ParsedData, scores: ExampleRatings): string {
  const headers = [
    'Example ID',
    'Example Text',
    'Model Name',
    'Inference Time (s)',
    'Entity Count',
    'Entities Found',
    'Entity Positions',
    'Confidence Scores',
    'Avg Confidence',
    'Rating (1-5)',
    'Notes'
  ];
  const rows: string[] = [headers.join(',')];

  data.examples.forEach(example => {
    // Add row for human annotations if they exist
    if (example.humanAnnotations && example.humanAnnotations.length > 0) {
      const humanEntities = example.humanAnnotations;
      const entityTexts = humanEntities.map(e => `${e.text}:${e.label}`).join('; ');
      const entityPositions = humanEntities
        .filter(e => e.start !== undefined && e.end !== undefined)
        .map(e => `(${e.start},${e.end})`)
        .join('; ');

      const row = [
        example.id,
        `"${example.text.replace(/"/g, '""')}"`,
        HUMAN_MODEL,
        '',
        humanEntities.length,
        `"${entityTexts.replace(/"/g, '""')}"`,
        `"${entityPositions}"`,
        '',
        '',
        '',
        ''
      ];
      rows.push(row.join(','));
    }

    // Add rows for each model
    data.modelNames.forEach(modelName => {
      const response = example.modelResponses.find(r => r.modelName === modelName);
      const score = scores[example.id]?.[modelName];

      // Collect entity information
      const entities = response?.entities || [];
      const entityTexts = entities.map(e => `${e.text}:${e.label}`).join('; ');
      const entityPositions = entities
        .filter(e => e.start !== undefined && e.end !== undefined)
        .map(e => `(${e.start},${e.end})`)
        .join('; ');
      const confidenceScores = entities
        .filter(e => e.confidence !== undefined)
        .map(e => e.confidence!.toFixed(4))
        .join('; ');
      const avgConfidence = entities.length > 0 && entities.some(e => e.confidence !== undefined)
        ? (entities.reduce((sum, e) => sum + (e.confidence || 0), 0) / entities.filter(e => e.confidence !== undefined).length).toFixed(4)
        : '';

      const row = [
        example.id,
        `"${example.text.replace(/"/g, '""')}"`,
        modelName,
        response?.inferenceTime !== undefined ? response.inferenceTime.toFixed(6) : '',
        entities.length,
        `"${entityTexts.replace(/"/g, '""')}"`,
        `"${entityPositions}"`,
        `"${confidenceScores}"`,
        avgConfidence,
        score?.rating || '',
        `"${(score?.notes || '').replace(/"/g, '""')}"`
      ];
      rows.push(row.join(','));
    });
  });

  return rows.join('\n');
}

export function calculateModelSummaries(
  data: ParsedData,
  scores: ExampleRatings,
  examplesOverride?: Example[]
): ModelSummary[] {
  // BQ-19: when an override is supplied, stats reflect the export scope.
  // Inference/confidence stats are computed against a scoped ParsedData
  // shim so existing helpers that read `data.examples` operate on the
  // subset without changing their signatures.
  const scopedExamples = examplesOverride ?? data.examples;
  const scopedData: ParsedData = examplesOverride
    ? { ...data, examples: scopedExamples }
    : data;

  return data.modelNames.map(modelName => {
    const modelScores = scopedExamples
      .map(q => scores[q.id]?.[modelName])
      .filter(score => score !== undefined && (score as any).rating > 0) as { rating: number }[];

    const totalQuestions = modelScores.length;
    const averageRating = totalQuestions > 0
      ? modelScores.reduce((sum, score) => sum + score.rating, 0) / totalQuestions
      : 0;

    const categoryCounts = {
      Best: 0,
      Good: 0,
      Fair: 0,
      Poor: 0
    };

    modelScores.forEach(score => {
      const derived = ratingToCategory(score.rating);
      categoryCounts[derived]++;
    });

    const inferenceStats = getModelInferenceStats(scopedData, modelName);
    const confidenceStats = getModelConfidenceStats(scopedData, modelName);
    // BQ-13: P/R/F1 vs human gold annotations across all match modes.
    const metrics = computeModelMetrics(scopedExamples, modelName);

    return {
      modelName,
      averageRating,
      totalQuestions,
      categoryCounts,
      inferenceStats,
      confidenceStats,
      metrics
    };
  });
}
